"use client";

import React from "react";
import { Loader, MapPin, Phone, Mail } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
const faqs = [{
  q: "How do I book an appointment for my pet?",
  a: "You can book directly through our website using the 'Book an Appointment' button, or call our clinic during business hours. We recommend booking at least 48 hours in advance for routine check-ups."
}, {
  q: "What should I bring to my first visit?",
  a: "Please bring any previous medical records, vaccination history, and a list of current medications or supplements your pet is taking."
}, {
  q: "Do you handle emergency cases?",
  a: "Yes, we prioritize emergency cases during our operating hours. For after-hours emergencies, we provide contact details for the nearest 24/7 animal hospital."
}, {
  q: "What payment methods do you accept?",
  a: "We accept all major credit cards, debit cards, and digital payments. We also work with several pet insurance providers to facilitate direct claims."
}];
const productOptions = ["General Wellness Exam", "Vaccinations", "Surgical Consultation", "Dental Care", "Other Services"];
function InquiryFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 rounded-2xl border border-border bg-card p-8 text-center shadow-lg">
        <div className="flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Mail className="size-6" />
        </div>
        <h3 className="font-default text-xl font-semibold text-card-foreground">
          Message Received
        </h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-lg">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default">Pet Owner Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane Smith" className="rounded-md border-input focus:ring-primary/50" {...field} />
              </FormControl>
              <FormDescription>Enter your full legal name</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="rounded-md border-input focus:ring-primary/50" {...field} />
              </FormControl>
              <FormDescription>We'll send appointment confirmations here</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default">Contact Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 000-0000" className="rounded-md border-input focus:ring-primary/50" {...field} />
              </FormControl>
              <FormDescription>For urgent updates regarding your pet</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default">Subject</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Vaccination Inquiry, New Patient" className="rounded-md border-input focus:ring-primary/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="product_interest" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default">Service of Interest</FormLabel>
            <FormControl>
              <select className="flex h-11 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-all outline-none focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm" {...field}>
                <option value="" disabled>
                  Select a veterinary service
                </option>
                {productOptions.map((option, i) => <option key={i} value={option}>
                    {option}
                  </option>)}
              </select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default">How can we help your pet?</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your pet's needs or symptoms..." className="min-h-[120px] resize-y rounded-md border-input focus:ring-primary/50" {...field} />
            </FormControl>
            <FormDescription>
              Include your pet's breed and age if possible.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-lg font-default transition-all shadow-md hover:shadow-lg active:scale-95">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Sending Message...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function SendAMessage() {
  return <section data-section-id="228693" id="send-a-message" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 text-center md:mb-16">
          <h2 className="font-default text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            Get in Touch
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-xl text-muted-foreground font-default">
            Have questions about our veterinary services or want to schedule a visit? Our friendly team is here to support you and your furry family members.
          </p>
        </div>

        <div className="grid gap-12 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-5 flex flex-col gap-10">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-chart-2/10 text-chart-2 text-sm font-semibold mb-4">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-lg bg-chart-2 opacity-75"></span>
                  <span className="relative inline-flex rounded-lg h-2 w-2 bg-chart-2"></span>
                </span>
                Replies in 2 hours
              </div>
              <h3 className="font-default text-2xl font-semibold text-foreground mb-6">
                Clinic Information
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary shadow-sm">
                    <Phone className="size-6" />
                  </div>
                  <div>
                    <p className="font-default font-semibold text-foreground text-lg">Phone Support</p>
                    <p className="text-muted-foreground text-lg">(555) 123-4567</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary shadow-sm">
                    <MapPin className="size-6" />
                  </div>
                  <div>
                    <p className="font-default font-semibold text-foreground text-lg">Our Location</p>
                    <p className="text-muted-foreground text-lg">123 Pet Lane, Animal Valley, CA</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-default text-2xl font-semibold text-foreground mb-6">
                Common Questions
              </h3>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border">
                    <AccordionTrigger className="font-default text-lg font-medium hover:text-primary text-left">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-border bg-card p-6 shadow-xl sm:p-10">
              <h3 className="font-default text-3xl font-semibold text-card-foreground mb-8">
                Send a Message
              </h3>
              <FormProvider formKey="send_message_form_contact" formId="send_message_form_contact" sectionName="send-a-message" defaultValues={{
              name: "",
              email: "",
              phone: "",
              subject: "",
              product_interest: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              message: {
                required: true
              }
            }} submitText="Send Message" successMessage="Thank you for contacting Happy Paws Animal Hospital. We've received your message and one of our care coordinators will reach out to you shortly.">
                <InquiryFormContent />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}